import React, { useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import useRoutePathForScreen from './useRoutePath';
import { getNextState } from './urlState';
import { HostPlatform } from '../config/hostPlatform';
import { SCREENS } from '../constants';
import { getMyself } from '../services/users';
import { GlobalContext } from '../context/GlobalState';
import { Board, Settings, Workspace } from '../containers';
import { LoadingPage } from '../components';
import {
  AzureNavigationService,
  JiraNavigationService,
} from './navigationService';

function useSyncCurrentUserSentry() {
  const { onLoadUser } = useContext(GlobalContext);
  const isAzure = HostPlatform.isAzure();
  const isMonday = HostPlatform.isMonday();
  const isJira = HostPlatform.isJira();

  useEffect(() => {
    (async () => {
      const currentUser = await getMyself();
      onLoadUser(currentUser);

      Sentry.setUser({
        id: currentUser.id,
        isAzure: isAzure,
        isMonday: isMonday,
        isJira: isJira,
      });
    })();
  }, [isAzure, isJira, isMonday, onLoadUser]);
}

function useRouteSyncing(navigationService) {
  const currentLocalPath = useRoutePathForScreen();
  const { onChangeScreen } = useContext(GlobalContext);

  useEffect(
    function syncScreenChangesToBrowser() {
      if (!navigationService) return;
      (async () => {
        const currentBrowserHash = await navigationService.getHash();
        const nextState = getNextState(currentBrowserHash);
        if (!currentBrowserHash || !nextState.valid) {
          navigationService.replaceHash(nextState.url);
        } else if (!currentLocalPath) {
          onChangeScreen(nextState.screen, nextState.retrospectiveId);
        } else if (currentLocalPath !== currentBrowserHash)
          navigationService.pushHash(currentLocalPath);
      })();
    },
    [currentLocalPath, onChangeScreen, navigationService]
  );

  useEffect(
    function syncBrowserChangesToScreen() {
      if (!navigationService) return;
      navigationService.onHashChanged((hash) => {
        if (hash === currentLocalPath) return;
        const nextState = getNextState(hash);
        if (nextState.valid) {
          onChangeScreen(nextState.screen, nextState.retrospectiveId);
        } else {
          navigationService.pushHash(nextState.url);
        }
      });
    },
    [navigationService, onChangeScreen, currentLocalPath]
  );
}

function Routes() {
  const isAzure = HostPlatform.isAzure();
  const isMonday = HostPlatform.isMonday();
  const isJira = HostPlatform.isJira();
  const [navigationService, setNavigationService] = useState();
  const { user, onChangeScreen, screen } = useContext(GlobalContext);

  useSyncCurrentUserSentry();
  useRouteSyncing(navigationService);

  useEffect(() => {
    (async () => {
      if (!isJira && !isAzure) return;
      const navigationService = isJira
        ? new JiraNavigationService()
        : new AzureNavigationService();
      await navigationService.init();
      setNavigationService(navigationService);
    })();
  }, [isJira, isAzure]);

  useEffect(() => {
    if (isMonday) {
      onChangeScreen(SCREENS.WORKSPACE);
    }
  }, [isMonday, onChangeScreen]);

  if (!user) {
    return <LoadingPage />;
  }
  if (screen === SCREENS.BOARD) {
    return <Board />;
  }
  if (screen === SCREENS.SETTINGS) {
    return <Settings />;
  }
  if (screen === SCREENS.WORKSPACE) {
    return <Workspace />;
  }

  return <p>Loading...</p>;
}

export default Routes;
